import React from 'react'
import { useLocation, useParams, useNavigate, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import i18next from 'i18next'

import MuiLink from '@mui/material/Link'
import Container from '@mui/material/Container'
import CircularProgress from '@mui/material/CircularProgress'
import Backdrop from '@mui/material/Backdrop'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'

import Alert from '../components/alert/alert'
import { get, patch, post, remove, setPageTitle } from '../utils/service'
import { Profile } from '../types/profile'
import ProfileEditor from '../components/profileEditor/profileEditor'
import {
  convertProfileToDisplayUnits,
  convertProfileToSaveUnits,
  createSlotPayload,
  generateShareUrl,
  getDefaultBbpProfileData,
  getDefaultProfileData,
  getNextFreeSlotIndex,
  getValidProfileTypeForMachine,
  isSharedProfile,
  isValidProfileTypeForMachine,
} from '../utils/profileEditorUtils'
import { ProfileColor, ProfileTypeString } from '../types/enums/profileType'
import { useContextHook } from '../store/context'
import { PagedResponse } from '../types/pagedResponse'
import { Machine } from '../types/machine'
import { MachineSlot } from '../types/machineslot'
import SlotAssignmentReplace from '../components/profiles/slotAssignmentReplace'
import { setCurrentMachine } from '../store/actions'
import ConfirmationDialog from '../components/dialog/confirmationDialog'
import { ProfileEditorSeries } from '../types/profileEditorSeries'
import ShareProfileDialog from '../components/profileEditor/shareProfileDialog'

enum SaveResult {
  None = 1,
  Created = 2,
  Updated = 3,
  NoChanges = 4,
}

interface ProfileSaveError {
  temperature_bezier?: Array<string>
  power_bezier?: Array<string>
  fan_bezier?: Array<string>
  rpm_bezier?: Array<string>
  detail?: string
}

export const formatProfileSaveError = (error: ProfileSaveError): string => {
  const message = (() => {
    if (error.temperature_bezier) {
      return i18next.t('editprofileview.temperature') + ': ' + error.temperature_bezier.join(', ')
    } else if (error.power_bezier) {
      return i18next.t('editprofileview.power') + ': ' + error.power_bezier.join(', ')
    } else if (error.fan_bezier) {
      return i18next.t('editprofileview.fanspeed') + ': ' + error.fan_bezier.join(', ')
    } else if (error.rpm_bezier) {
      return i18next.t('editprofileview.drumrpm') + ': ' + error.rpm_bezier.join(', ')
    } else if (error.detail) {
      return error.detail
    }
  })()

  if (message) {
    return i18next.t('editprofileview.profilesaveerror') + ': ' + message
  } else {
    return i18next.t('editprofileview.profilesaveerror')
  }
}

const CreateProfileView = (): JSX.Element => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const locationState = location.state as {
    profileData?: Profile
    logSeries?: ProfileEditorSeries
    showAddToLibrarySuccess?: boolean
  }
  const { state, dispatch } = useContextHook()
  const params = useParams()

  const [isLoadingProfile, setIsLoadingProfile] = React.useState<boolean>(false)
  const [hasLoadError, setHasLoadError] = React.useState<boolean>(false)
  const [profileData, setProfileData] = React.useState<Profile | null>(null)
  const [loadedProfileData, setLoadedProfileData] = React.useState<Profile | null>(null)
  const [isProfileTypeSelectorVisible, setIsProfileTypeSelectorVisible] = React.useState<boolean>(false)

  const [isSaving, setIsSaving] = React.useState<boolean>(false)
  const [saveError, setSaveError] = React.useState<false | ProfileSaveError>(false)
  const [hasAddToLibraryError, setHasAddToLibraryError] = React.useState<boolean>(false)
  const [hasAddToLibrarySuccess, setHasAddToLibrarySuccess] = React.useState<boolean>(false)
  const [saveResult, setSaveResult] = React.useState<SaveResult>(SaveResult.None)

  const [hasDeleteProfileSuccess, setHasDeleteProfileSuccess] = React.useState<boolean>(false)
  const [hasDeleteProfileError, setHasDeleteProfileError] = React.useState<boolean>(false)
  const [isAssigningProfile, setIsAssigningProfile] = React.useState<boolean>(false)
  const [assignmentSuccessSlot, setAssignmentSuccessSlot] = React.useState<string>('')
  const [hasAssignmentError, setHasAssignmentError] = React.useState<boolean>(false)
  const [machineSlots, setMachineSlots] = React.useState<Array<MachineSlot> | null>(null)
  const [isReplaceBbpProfileModalOpen, setIsReplaceBbpProfileModalOpen] = React.useState<boolean>(false)

  const [saveBeforeShareProfile, setSaveBeforeShareProfile] = React.useState<Profile | null>(null)
  const [isShareDialogOpen, setIsShareDialogOpen] = React.useState<boolean>(false)
  const [profileRemovedFromLibrary, setProfileRemovedFromLibrary] = React.useState<Profile | null>(null)
  const [sharedProfile, setSharedProfile] = React.useState<Profile | null>(null)
  const [profileAddedToLibrary, setProfileAddedToLibrary] = React.useState<Profile | null>(null)

  // update page title
  React.useEffect(() => {
    let pageTitle = ''
    if (profileData?.name) {
      if (profileData?.is_bbp_profile) {
        pageTitle = `${t('editprofileview.bbppagetitle')} ${profileData.name}`
      } else {
        pageTitle = `${t('editprofileview.profilepagetitle')} ${profileData.name}`
      }
    } else {
      if (profileData?.is_bbp_profile) {
        pageTitle = t('editprofileview.newbbpprofile')
      } else {
        pageTitle = t('editprofileview.newprofile')
      }
    }

    setPageTitle(pageTitle)
  }, [profileData?.name, profileData?.is_bbp_profile, t])

  const setNewProfileData = React.useCallback((newProfileData: Profile) => {
    setLoadedProfileData(newProfileData) // set loaded data to use to check for changes when saving
    // convert all temperature values to Fahrenheit, if temp display is Fahrenheit
    const convertedProfileData = convertProfileToDisplayUnits(newProfileData)
    setProfileData(convertedProfileData)
  }, [])

  const loadProfileData = React.useCallback((): void => {
    const loadFailed = () => {
      setIsLoadingProfile(false)
      setHasLoadError(true)
    }
    if (locationState?.showAddToLibrarySuccess) {
      setHasAddToLibrarySuccess(true)
    }
    if (!!params.profileId) {
      // load profile currently in our library
      setIsLoadingProfile(true)
      get<Profile>(`profiles/${params.profileId}/`)
        .then((data) => {
          setIsLoadingProfile(false)
          setNewProfileData(data.data)
        })
        .catch(loadFailed)
    } else if (!!params.sharedProfileId) {
      // load shared profile with share_uuid:
      get<PagedResponse<Profile>>(`profiles/?share_uuid=${params.sharedProfileId}`)
        .then((data) => {
          setIsLoadingProfile(false)
          if (data.data.results.length > 0) {
            setNewProfileData(data.data.results[0])
          } else {
            loadFailed()
          }
        })
        .catch(loadFailed)
    } else {
      if (locationState?.profileData) {
        // create profile based on log data; all profile data is in locationState
        setNewProfileData(locationState.profileData)
      } else {
        // create new profile
        setIsProfileTypeSelectorVisible(true)
      }
    }
  }, [
    locationState?.profileData,
    locationState?.showAddToLibrarySuccess,
    params.profileId,
    params.sharedProfileId,
    setNewProfileData,
  ])

  React.useEffect(() => {
    if (state.user && !profileData?.id) {
      loadProfileData()
    }
  }, [state.user, profileData?.id, loadProfileData]) // we must load user (to determine if darkmode is enabled or not) before rendering chart

  const hasChanges = (savePayload: Profile): boolean => {
    if (isSharedProfile(savePayload) || !savePayload.is_leaf) {
      return false
    }
    // we don't differentiate whether _bezier arrays are null or empty arrays
    const toCompareObject = (profileToChange: Profile): Partial<Profile> => {
      return {
        name: profileToChange.name,
        preheat_temperature: profileToChange.preheat_temperature,
        batch_weight: profileToChange.batch_weight,
        end_condition: profileToChange.end_condition,
        end_condition_value: profileToChange.end_condition_value,
        notes: profileToChange.notes,
        selected_filters: (profileToChange.selected_filters || [])
          .map((x) => {
            return { id: x.id, label: '' }
          })
          .sort((a, b) => a.id - b.id),
        temperature_bezier: (profileToChange.temperature_bezier || []).map(([x1, y1]) => [x1, Math.round(y1)]),
        power_bezier: profileToChange.power_bezier || [],
        fan_bezier: profileToChange.fan_bezier || [],
        rpm_bezier: profileToChange.rpm_bezier || [],
      }
    }
    return (
      JSON.stringify(toCompareObject(savePayload)) !==
      JSON.stringify(loadedProfileData ? toCompareObject(loadedProfileData) : null)
    )
  }

  const onSaveProfileSuccess = (savedData: Profile, result: SaveResult, isAssignedBbpProfileSaved: boolean): void => {
    setIsSaving(false)

    const machineSlug = params.machineId
    navigate(`/machine/${machineSlug}/profiles/${savedData.id}`, { replace: true })

    setSaveResult(result)
    setNewProfileData(savedData)
    // if the current assigned bbp profile was updated, set the updated profile in machine
    if (isAssignedBbpProfileSaved) {
      dispatch(setCurrentMachine({ ...(state.currentMachine as Machine), bbp_profile: savedData }))
    }
  }

  const saveProfile = (profileDataToSave: Profile, isNewProfile: boolean, shareAfterSave?: boolean): void => {
    setSaveBeforeShareProfile(null)
    // convert all values to Celsius if user is using Fahrenheit:
    const profileDataPayload = convertProfileToSaveUnits(profileDataToSave)
    if (isNewProfile || hasChanges(profileDataPayload)) {
      setIsSaving(true)
      resetAlerts()

      const postUrl = isNewProfile && profileDataPayload.id > 0 ? 'profiles/branch/' : 'profiles/'
      post<Profile, Profile>(postUrl, profileDataPayload)
        .then((savedProfileData) => {
          const isAssignedBbpProfileSaved =
            !!profileDataToSave.url && profileDataToSave.url === state.currentMachine?.bbp_profile?.url
          onSaveProfileSuccess(
            savedProfileData.data,
            isNewProfile ? SaveResult.Created : SaveResult.Updated,
            isAssignedBbpProfileSaved
          )
          if (shareAfterSave) {
            setIsShareDialogOpen(true)
          }
        })
        .catch((error) => {
          setIsSaving(false)
          if (error.isAxiosError) {
            setSaveError(error.response.data)
          } else {
            setSaveError({})
          }
        })
    } else {
      // no changes to save, just show save confirmation message
      setSaveResult(SaveResult.NoChanges)
    }
  }

  const addProfileToLibrary = (profile: Profile): void => {
    setIsSaving(true)
    setHasAddToLibraryError(false)
    // load machines so we have a machine slug to use in url
    get<Array<Machine>>(`/machines/?owner=${state.currentCustomer?.id}&page_size=all`)
      .then((loadData) => {
        const firstValidMachine = loadData.data[0]
        // send PATCH to add this profile in customers library
        return patch<{ import_profile: string }, {}>(state.currentCustomer?.url || '', {
          import_profile: profile.url,
        }).then(() => {
          // GET the profile that was just added with share_uuid, use id of this profile + machine slug to navigate to new profile
          return get<PagedResponse<Profile>>(`profiles/?share_uuid=${params.sharedProfileId}`).then((profileData) => {
            setIsSaving(false)
            setHasAddToLibrarySuccess(true)
            navigate(`/machine/${firstValidMachine.slug}/profiles/${profileData.data.results[0].id}`, { replace: true })
          })
        })
      })
      .catch(() => {
        setIsSaving(false)
        setHasAddToLibraryError(true)
      })
  }

  const resetAlerts = (): void => {
    setSaveResult(SaveResult.None)
    setHasAddToLibrarySuccess(false)
    setSaveError(false)
    setHasAssignmentError(false)
    setAssignmentSuccessSlot('')
  }

  const deleteProfile = (profile: Profile): void => {
    setIsSaving(true)
    setHasDeleteProfileError(false)
    resetAlerts()

    // if profile is shared, customer_id must also be sent in request:
    const requestUrl = isSharedProfile(profile) ? `${profile.url}?customer=${state.currentCustomer?.id}` : profile.url

    remove(requestUrl)
      .then(() => {
        setHasDeleteProfileSuccess(true)
        setIsSaving(false)
      })
      .catch(() => {
        setHasDeleteProfileError(true)
        setIsSaving(false)
      })
  }

  const assignProfileToSlot = (slotIndex: number): void => {
    // assign profile to selected slot
    setIsAssigningProfile(true)
    const machineSlotToAdd = createSlotPayload(profileData, slotIndex, state.currentMachine?.url || '')
    post<MachineSlot, MachineSlot>('machineslots/', machineSlotToAdd)
      .then((response) => {
        setIsAssigningProfile(false)
        setAssignmentSuccessSlot(`${response.data.slot_index + 1}`)
        setSaveResult(SaveResult.None)
        setHasAddToLibrarySuccess(false)
      })
      .catch(() => {
        setIsAssigningProfile(false)
        setSaveResult(SaveResult.None)
        setHasAddToLibrarySuccess(false)
        setHasAssignmentError(true)
      })
  }

  const sendAssignBbpRequest = (): void => {
    setIsReplaceBbpProfileModalOpen(false)
    setIsAssigningProfile(true)
    post<{ bbp_profile: string }, Machine>(`${state.currentMachine?.url}bbp_profile/`, {
      bbp_profile: profileData?.url || '',
    })
      .then((response) => {
        setIsAssigningProfile(false)
        setAssignmentSuccessSlot(t('profilesview.bbpprofile'))
        setSaveResult(SaveResult.None)
        setHasAddToLibrarySuccess(false)
        dispatch(setCurrentMachine(response.data))
      })
      .catch(() => {
        setIsAssigningProfile(false)
        setSaveResult(SaveResult.None)
        setHasAddToLibrarySuccess(false)
        setHasAssignmentError(true)
      })
  }

  const onAssignBppProfile = (): void => {
    if (state.currentMachine?.bbp_profile) {
      setIsReplaceBbpProfileModalOpen(true)
    } else {
      sendAssignBbpRequest()
    }
  }

  const onAssignToSlot = (): void => {
    setIsAssigningProfile(true)
    if (state.currentMachine?.id) {
      get<PagedResponse<MachineSlot>>(`machineslots/?machine=${state.currentMachine.id}`)
        .then((response) => {
          // check if there is a free slot.
          const freeSlotIndex = getNextFreeSlotIndex(state.currentMachine, response.data.results)
          if (freeSlotIndex !== -1) {
            // assign profile to free slot
            assignProfileToSlot(freeSlotIndex)
          } else {
            // display dialog so user can choose which slot to assign profile
            setIsAssigningProfile(false) // hide spinner while dialog is open
            const slots = [...response.data.results]
            slots.sort((a, b) => a.slot_index - b.slot_index)
            setMachineSlots(slots)
          }
        })
        .catch(() => {
          setIsAssigningProfile(false)
          setSaveResult(SaveResult.None)
          setHasAddToLibrarySuccess(false)
          setHasAssignmentError(true)
        })
    }
  }

  const generateAlertAction = (): JSX.Element => {
    const isValid = profileData && isValidProfileTypeForMachine(profileData, state.currentMachine)
    if (!isValid) {
      return <></>
    }
    return isAssigningProfile ? (
      <CircularProgress />
    ) : (
      <Button size="small" onClick={profileData.is_bbp_profile ? onAssignBppProfile : onAssignToSlot}>
        {profileData.is_bbp_profile ? t('editprofileview.assignbbp') : t('editprofileview.assigntoslot')}
      </Button>
    )
  }

  const isSharedProfilePreview = (): boolean => {
    return window.location.pathname.indexOf('shared_profile') > -1
  }

  const updateProfileAfterShare = (changedProfile: Profile): void => {
    setIsShareDialogOpen(false)
    setNewProfileData(changedProfile)
  }

  const onShareProfile = (profileToShare: Profile): void => {
    // check if this profile has unsaved changes before sharing
    if (hasChanges(convertProfileToSaveUnits(profileToShare))) {
      setSaveBeforeShareProfile(profileToShare)
    } else {
      setIsShareDialogOpen(true)
    }
  }

  return (
    <Container
      component="main"
      maxWidth={false}
      sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}
    >
      {machineSlots && profileData && (
        <SlotAssignmentReplace
          assignedSlots={machineSlots}
          newProfileName={profileData.name}
          onClose={() => setMachineSlots(null)}
          onReplace={(slotToReplaceUrl: string) => {
            const existingSlot = machineSlots.find((x) => x.url === slotToReplaceUrl)
            if (existingSlot) {
              assignProfileToSlot(existingSlot.slot_index)
            }
            setMachineSlots(null)
          }}
        />
      )}
      {profileData && isReplaceBbpProfileModalOpen && (
        <ConfirmationDialog
          header={t('profilesview.replacebbpprofile')}
          body={t('profilesview.replacebbpprofilebody')
            .replace('{0}', state.currentMachine?.bbp_profile?.name || '')
            .replace('{1}', profileData.name)}
          confirmButtonText={t('profilesview.replace')}
          onConfirm={() => sendAssignBbpRequest()}
          onClose={() => setIsReplaceBbpProfileModalOpen(false)}
        />
      )}
      {saveBeforeShareProfile && (
        <ConfirmationDialog
          header={t('editprofileview.saveprofilebeforesharing')}
          body={t('editprofileview.savebeforesharebody')}
          confirmButtonText={t('editprofileview.updateprofile')}
          onConfirm={() => saveProfile(saveBeforeShareProfile, false, true)}
          onClose={() => setSaveBeforeShareProfile(null)}
        />
      )}
      {isShareDialogOpen && profileData && (
        <ShareProfileDialog
          currentCustomerUrl={state.currentCustomer?.url || ''}
          profile={profileData}
          onClose={() => setIsShareDialogOpen(false)}
          onShareLinkCreated={(shareUuid: string) => {
            const profileShared = {
              ...profileData,
              share_uuid: shareUuid,
            }
            setSharedProfile(profileShared)
            updateProfileAfterShare(profileShared)
          }}
          onRemovedFromLibrary={() => {
            const profileShared = {
              ...profileData,
              public_share_disabled: true,
            }
            setProfileRemovedFromLibrary(profileShared)
            updateProfileAfterShare(profileShared)
          }}
          onAddedToLibrary={(publicShareUuid: string) => {
            const profileShared = {
              ...profileData,
              public_share_uuid: publicShareUuid,
              public_share_disabled: false,
            }
            setProfileAddedToLibrary(profileShared)
            updateProfileAfterShare(profileShared)
          }}
        />
      )}
      <Box sx={{ mb: 0.5 }}>
        <Backdrop open={isSaving} sx={{ zIndex: 10 }}>
          <CircularProgress />
        </Backdrop>
        {isProfileTypeSelectorVisible && (
          <Stack spacing={1} sx={{ maxWidth: 340, marginLeft: 'auto', marginRight: 'auto' }}>
            <Typography>{t('editprofileview.chooseprofiletype')}</Typography>
            {getValidProfileTypeForMachine(state.currentMachine, false).map((x) => {
              return (
                <Button
                  key={x}
                  variant="outlined"
                  color={ProfileColor[x]}
                  onClick={() => {
                    const profileData = getDefaultProfileData(x)
                    setNewProfileData(profileData)
                    setIsProfileTypeSelectorVisible(false)
                  }}
                >
                  {t(ProfileTypeString[x])}
                </Button>
              )
            })}
            <Typography sx={{ pt: 3 }}>{t('editprofileview.chooseprofiletypebbp')}</Typography>
            {getValidProfileTypeForMachine(state.currentMachine, true).map((x) => {
              return (
                <Button
                  key={x}
                  variant="outlined"
                  color={ProfileColor[x]}
                  onClick={() => {
                    const profileData = getDefaultBbpProfileData(x, '')
                    setNewProfileData(profileData)
                    setIsProfileTypeSelectorVisible(false)
                  }}
                >
                  {t(ProfileTypeString[x])}
                </Button>
              )
            })}
          </Stack>
        )}
        {profileAddedToLibrary && (
          <Alert severity="success">
            {t('profilesview.profileaddedtolibrary').replace('{0}', profileAddedToLibrary.name)}
          </Alert>
        )}
        {profileRemovedFromLibrary && (
          <Alert severity="info">
            {t('profilesview.profileremovedfromlibrary').replace('{0}', profileRemovedFromLibrary.name)}
          </Alert>
        )}
        {sharedProfile && (
          <Alert severity="success">
            <>
              {t('profilesview.sharesuccess').replace('{0}', sharedProfile.name)}
              {` `}
              <MuiLink href={generateShareUrl(sharedProfile.share_uuid || '')}>
                {generateShareUrl(sharedProfile.share_uuid || '')}
              </MuiLink>
            </>
          </Alert>
        )}
        {isSharedProfilePreview() && <Alert severity="info">{t('editprofileview.sharedprofile')}</Alert>}
        {!isSharedProfilePreview() && profileData && !profileData.is_leaf && (
          <Alert severity="info">{t('editprofileview.viewhistoric')}</Alert>
        )}
        {!isSharedProfilePreview() &&
          !hasAddToLibrarySuccess &&
          isSharedProfile(profileData) &&
          !assignmentSuccessSlot && <Alert severity="info">{t('editprofileview.sharedprofileinfo')}</Alert>}
        {saveError && <Alert severity="error">{formatProfileSaveError(saveError)}</Alert>}
        {saveResult === SaveResult.Created && profileData && (
          <Alert severity="success" action={generateAlertAction()}>
            {t('editprofileview.profilecreated').replace('{0}', profileData.name)}
          </Alert>
        )}
        {saveResult === SaveResult.Updated && profileData && (
          <Alert severity="success">{t('editprofileview.profilesaved').replace('{0}', profileData.name)}</Alert>
        )}
        {saveResult === SaveResult.NoChanges && <Alert severity="info">{t('editprofileview.nochangestosave')}</Alert>}
        {hasAddToLibraryError && <Alert severity="error">{t('editprofileview.addtolibraryerror')}</Alert>}
        {hasAddToLibrarySuccess && (
          <Alert severity="success" action={generateAlertAction()}>
            {`${t('editprofileview.addtolibrarysuccess')} ${t('editprofileview.sharedprofileinfo')}`}
          </Alert>
        )}
        {isLoadingProfile && <CircularProgress />}
        {hasLoadError && <Alert severity="error">{t('editprofileview.loaderror')}</Alert>}
        {hasAssignmentError && <Alert severity="error">{t('editprofileview.assignprofileerror')}</Alert>}
        {!!assignmentSuccessSlot && profileData && (
          <Alert severity="success">
            {t('editprofileview.profileassignedtoslot')
              .replace('{0}', profileData.name)
              .replace('{1}', assignmentSuccessSlot)}
          </Alert>
        )}
        {hasDeleteProfileSuccess && profileData && (
          <Stack spacing={2} alignItems="flex-start">
            <Alert severity="success">
              {state.currentCustomer?.url === profileData.customer
                ? t('editprofileview.profiledeleted').replace('{0}', profileData.name)
                : t('editprofileview.profileremoved').replace('{0}', profileData.name)}
            </Alert>
            <Button
              component={Link}
              sx={{ textAlign: 'center' }}
              to={`/machine/${state.currentMachine?.slug}/profiles/`}
            >
              {t('editprofileview.backtoprofiles')}
            </Button>
          </Stack>
        )}
        {hasDeleteProfileError && profileData && (
          <Alert severity="error">
            {state.currentCustomer?.url === profileData.customer
              ? t('editprofileview.deleteprofileError')
              : t('editprofileview.removeprofileError')}
          </Alert>
        )}
      </Box>
      {profileData && state.user && !hasDeleteProfileSuccess && (
        <ProfileEditor
          profileData={profileData}
          logSeries={locationState?.logSeries}
          isSharePreview={isSharedProfilePreview()}
          customerUrl={state.currentCustomer?.url || ''}
          customerId={state.currentCustomer?.id || 0}
          machine={state.currentMachine}
          user={state.user}
          saveProfile={saveProfile}
          deleteProfile={deleteProfile}
          addProfileToLibrary={addProfileToLibrary}
          onShareProfile={onShareProfile}
          hasChanges={hasChanges}
        />
      )}
    </Container>
  )
}

export default CreateProfileView
